import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { Button, Dropdown } from '../components/ui';
import PayExpenseBillModal from '../components/PayExpenseBillModal';
import AddSaleExpenseModal from '../components/AddSaleExpenseModal';
import { HeaderSearch, DateRangeButton, ChoiceButton, StatChips, IconExportSm, IconPlusSm } from '../components/HeaderControls';
import { useSettings } from '../hooks/useSettings';
import { downloadCSV } from '../utils/csv';
import { fmtMoney } from '../utils/currency';
import { computeDateWindow, inWindow, type DateFilterKey } from '../utils/ledgerReports';
import type { SaleExpense, Entity, BankAccount } from '../types';

// ── Sales Expenses — every saleExpenses doc across all sales ────────────────
// Entered from the sale (Add/Edit Sale › "Additional Charges & Expenses")
// or here with Add Expense (AddSaleExpenseModal), which adds one to a saved
// sale through the same save path. Changing / removing one stays on the sale. Layout mirrors PurchaseExpenses.tsx.

// createdAt is written via serverTimestamp() (a Firestore Timestamp once
// synced back) even though the app's own types declare it as `string` — a
// plain .localeCompare() on it throws and crashes the page the moment two
// rows share the same date and the sort's tie-breaker actually runs. Reads
// either shape (Timestamp instance, plain {seconds}, or an ISO string)
// into sortable millis instead. Mirrors Dashboard.tsx's own toMillis.
function toMillis(v: unknown): number {
  if (!v) return 0;
  if (typeof v === 'string') return new Date(v).getTime();
  const obj = v as { toMillis?: () => number; seconds?: number };
  if (typeof obj.toMillis === 'function') return obj.toMillis();
  if (typeof obj.seconds === 'number') return obj.seconds * 1000;
  return 0;
}
const fmtDate = (s?: string) =>
  s ? new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
// A code is stored in full ("SE-2026-0001"), but the list only shows the
// prefix + last 4 digits to stay compact — same convention as Purchase.tsx's
// lotNoDisplay. The full value is always in a `title` tooltip next to it.
function codeDisplay(code?: string) {
  if (!code) return '—';
  const m = code.match(/^([A-Za-z]+)-\d+-(\d+)$/);
  return m ? `${m[1]}-${m[2].slice(-4)}` : code.slice(-4);
}
function IdPill({ children }: { children: ReactNode }) {
  return (
    <span style={{
      display: 'inline-block', fontFamily: 'var(--font-num)', background: 'var(--primary-light)',
      color: 'var(--primary)', fontSize: 11, fontWeight: 700, padding: '3px 8px',
      borderRadius: 6, letterSpacing: '0.02em',
    }}>
      {children}
    </span>
  );
}

const PAGE_SIZE = 50;

// ── Pagination (same pattern as Purchase/Sales/Inventory/Entity/Memos/AP/AR/SalesReturns) ──
function Pagination({ current, total, totalItems, pageSize, onChange }: {
  current: number; total: number; totalItems: number; pageSize: number; onChange: (p: number) => void;
}) {
  if (total <= 1) return null;
  const from = (current - 1) * pageSize + 1;
  const to   = Math.min(current * pageSize, totalItems);
  const pages: (number | 'e')[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3)         pages.push('e');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 2) pages.push('e');
    pages.push(total);
  }
  return (
    <div className="pagination">
      <span className="pagination-info">
        Showing <strong>{from}–{to}</strong> of <strong>{totalItems}</strong> expense{totalItems !== 1 ? 's' : ''}
      </span>
      <div className="pagination-btns">
        <button className="pg-btn" disabled={current === 1} onClick={() => onChange(current - 1)}>‹</button>
        {pages.map((p, i) => p === 'e'
          ? <span key={`e${i}`} style={{ minWidth: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--text-3)' }}>…</span>
          : <button key={p} className={`pg-btn${p === current ? ' active' : ''}`} onClick={() => onChange(p)}>{p}</button>
        )}
        <button className="pg-btn" disabled={current === total} onClick={() => onChange(current + 1)}>›</button>
      </div>
    </div>
  );
}

type KindFilter = 'all' | 'own' | 'charged';

export default function SalesExpensesPage() {
  const { business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [expenses, setExpenses] = useState<SaleExpense[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [payTarget, setPayTarget] = useState<SaleExpense | null>(null);
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);

  const [dateKey, setDateKey] = useState<DateFilterKey>('this-month');
  const [searchQ, setSearchQ] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [kind, setKind] = useState<KindFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    const u1 = onSnapshot(collection(db, 'saleExpenses'),
      snap => { setExpenses(snap.docs.map(d => ({ id: d.id, ...d.data() } as SaleExpense))); setLoading(false); },
      () => setLoading(false));
    const u2 = onSnapshot(collection(db, 'entities'),
      snap => setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() } as Entity))), () => {});
    const u3 = onSnapshot(collection(db, 'bankAccounts'),
      snap => setAccounts(snap.docs.map(d => ({ id: d.id, ...d.data() } as BankAccount)).filter(a => a.isActive !== false)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))), () => {});
    return () => { u1(); u2(); u3(); };
  }, [business]);

  const window = useMemo(() => computeDateWindow(dateKey, customFrom, customTo), [dateKey, customFrom, customTo]);
  const entityName = (id?: string) => (id ? entities.find(e => e.id === id)?.companyName ?? '—' : '—');

  const rows = useMemo(() => {
    if (!window) return [];
    return expenses
      .filter(e => inWindow(e.date, window))
      .filter(e => kind === 'all' || (kind === 'charged') === !!e.chargeToCustomer)
      .filter(e => {
        const q = searchQ.trim().toLowerCase();
        return !q || [e.expenseNo, e.category, e.description, e.saleInvoiceNo, entityName(e.customerEntityId)]
          .some(v => (v ?? '').toLowerCase().includes(q));
      })
      .sort((a, b) => b.date.localeCompare(a.date) || toMillis(b.createdAt) - toMillis(a.createdAt));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenses, window, kind, searchQ, entities]);

  // Any filter change goes back to page 1 (in the handlers, not an effect).
  const andFirstPage = <T,>(set: (v: T) => void) => (v: T) => { set(v); setCurrentPage(1); };
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pagedRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Print covers every filtered row, not just this page (same as the other list pages).
  // globalThis, not window: `window` here is the page's date range.
  const [printAll, setPrintAll] = useState(false);
  useEffect(() => {
    if (!printAll) return;
    let cancelled = false;
    const after = () => setPrintAll(false);
    globalThis.addEventListener('afterprint', after);
    requestAnimationFrame(() => { if (!cancelled) requestAnimationFrame(() => { if (!cancelled) globalThis.print(); }); });
    return () => { cancelled = true; globalThis.removeEventListener('afterprint', after); };
  }, [printAll]);

  function handleExportCSV() {
    downloadCSV('sales-expenses',
      ['Date', 'Ref', 'Category', 'Description', 'Sale', 'Customer', 'Who pays', 'Amount', 'Status'],
      rows.map(e => [e.date, e.expenseNo, e.category, e.description, e.saleInvoiceNo ?? '', entityName(e.customerEntityId), e.chargeToCustomer ? 'Charged to customer' : 'Our cost', (e.amountUSD ?? 0).toFixed(2), e.paymentStatus ?? '']),
    );
  }

  const kpi = useMemo(() => {
    const inW = window ? expenses.filter(e => inWindow(e.date, window)) : [];
    const own = inW.filter(e => !e.chargeToCustomer).reduce((s, e) => s + (e.amountUSD ?? 0), 0);
    const charged = inW.filter(e => e.chargeToCustomer).reduce((s, e) => s + (e.amountUSD ?? 0), 0);
    const unpaid = expenses.filter(e => e.paymentStatus === 'unpaid').reduce((s, e) => s + (e.amountUSD ?? 0), 0);
    return { own, charged, unpaid };
  }, [expenses, window]);

  return (
    <AppLayout
      title="Sales Expenses"
      subtitle="Shipping, insurance, duty and the like on your sales — your own cost, or charged on to the customer. Add or change them from the sale itself."
      toolbar={<>
        <HeaderSearch value={searchQ} onChange={andFirstPage(setSearchQ)} placeholder="Ref, category, invoice, customer…" label="Search sales expenses" />
        <DateRangeButton
          value={dateKey} onChange={andFirstPage(setDateKey)}
          customFrom={customFrom} customTo={customTo}
          onCustomFrom={andFirstPage(setCustomFrom)} onCustomTo={andFirstPage(setCustomTo)}
        />
        <ChoiceButton<KindFilter>
          label="Who pays" value={kind} onChange={andFirstPage(setKind)} defaultKey="all"
          options={[{ key: 'all', label: 'All' }, { key: 'own', label: 'Our cost' }, { key: 'charged', label: 'Charged to customer' }]}
        />
        {(searchQ || dateKey !== 'this-month' || kind !== 'all') && (
          <button type="button" className="tb-clear" onClick={() => { setSearchQ(''); setDateKey('this-month'); setKind('all'); setCurrentPage(1); }}>Clear</button>
        )}
      </>}
      headerActions={<>
        <div className="tb-group">
          <Dropdown
            align="right"
            trigger={() => (
              <button type="button" className="tb-group-btn" title="Export" aria-label="Export"><IconExportSm /></button>
            )}
          >
            <button type="button" className="dropdown-item" onClick={handleExportCSV}>Export CSV</button>
            <button type="button" className="dropdown-item" onClick={() => setPrintAll(true)}>Print List</button>
          </Dropdown>
        </div>
        <Button variant="primary" size="sm" icon={<IconPlusSm />} onClick={() => setAdding(true)} disabled={!business}
          style={{ height: 34, boxSizing: 'border-box' }}>Add Expense</Button>
      </>}
    >
      <StatChips items={[
        { label: 'Our cost', value: money(kpi.own) },
        { label: 'Charged to customers', value: money(kpi.charged) },
        { label: 'Bills to pay', value: money(kpi.unpaid), tone: kpi.unpaid > 0 ? 'danger' : undefined },
      ]} />

      {loading ? (
        <div className="card"><div className="skeleton" style={{ height: 240, borderRadius: 8 }} /></div>
      ) : rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>No sales expenses here</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
            Add one from a sale: Sales › Add / Edit Sale › Additional Charges &amp; Expenses.
          </div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Date</th>
                  <th style={{ width: 120 }}>Ref</th>
                  <th style={{ width: 150 }}>Category</th>
                  <th>Description</th>
                  <th style={{ width: 120 }}>Sale</th>
                  <th style={{ width: 160 }}>Customer</th>
                  <th style={{ width: 150 }}>Who pays</th>
                  <th style={{ textAlign: 'right', width: 120 }}>Amount</th>
                  <th style={{ width: 80 }}>Status</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(printAll ? rows : pagedRows).map(e => (
                  <tr key={e.id}>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{fmtDate(e.date)}</td>
                    <td title={e.expenseNo}><IdPill>{codeDisplay(e.expenseNo)}</IdPill></td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{e.category}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>
                      {e.description}
                      {e.vendorEntityId && <span style={{ color: 'var(--text-3)' }}> · {entityName(e.vendorEntityId)}</span>}
                    </td>
                    <td>
                      {e.saleInvoiceNo ? <span title={e.saleInvoiceNo}><IdPill>{codeDisplay(e.saleInvoiceNo)}</IdPill></span> : <span style={{ opacity: 0.4 }}>—</span>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{entityName(e.customerEntityId)}</td>
                    <td>
                      <span className="badge" style={{
                        fontSize: 10, fontWeight: 700,
                        background: e.chargeToCustomer ? 'var(--primary-light)' : 'var(--surface-2)',
                        color: e.chargeToCustomer ? 'var(--primary)' : 'var(--text-3)',
                      }}>{e.chargeToCustomer ? 'Charged to customer' : 'Our cost'}</span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)', textAlign: 'right' }}>
                      {money(e.amountUSD)}
                    </td>
                    <td>
                      <span className="badge" style={{
                        fontSize: 10, fontWeight: 700,
                        background: e.paymentStatus === 'paid' ? 'var(--success-bg)' : 'var(--danger-bg)',
                        color: e.paymentStatus === 'paid' ? 'var(--success)' : 'var(--danger)',
                      }}>{e.paymentStatus === 'paid' ? 'Paid' : 'Unpaid'}</span>
                    </td>
                    <td className="col-actions">
                      {e.paymentStatus === 'unpaid'
                        ? <Button variant="secondary" size="sm" onClick={() => setPayTarget(e)}>Pay</Button>
                        : <span style={{ opacity: 0.4 }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <Pagination
          current={currentPage} total={totalPages}
          totalItems={rows.length} pageSize={PAGE_SIZE}
          onChange={setCurrentPage}
        />
      )}
      {adding && <AddSaleExpenseModal entities={entities} accounts={accounts} onClose={() => setAdding(false)} />}
      {payTarget && (
        <PayExpenseBillModal bill={{ kind: 'sale', expense: payTarget }} accounts={accounts} onClose={() => setPayTarget(null)} />
      )}
    </AppLayout>
  );
}
